import {
  ProviderProps,
  ReactElement,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';

import { Nullable } from '~globals/types/commons';
import { ApiTransactionRoutes } from '~services/apiTransaction/types';

import { AssistantGenerateRoutesDialogTab } from '../types';

import {
  AssistantGenerateRoutesDialogByTemplateContextValue,
  AssistantGenerateRoutesDialogByTemplateProviderProps,
} from './types';

const AssistantGenerateRoutesDialogByTemplateContext =
  createContext<AssistantGenerateRoutesDialogByTemplateContextValue>({
    onClose: () => {},
    activeTab: { tab: 'form', setActiveTab: () => {} },
    data: {
      templates: [],
      selectedTemplate: {
        value: null,
        setValue: () => {},
      },
      selectedDriver: {
        value: null,
        setValue: () => {},
      },
      warehouses: [],
    },
    form: {
      isScheduleRouteEnabled: {
        value: false,
        setValue: () => {},
      },
    },
    stepper: {
      currentApiTransactionId: {
        value: null,
        setCurrentApiTransactionId: () => {},
      },
      currentRoutes: {
        value: [],
        setCurrentRoutes: () => {},
      },
      isSuccesCompleted: {
        value: false,
        setIsSuccesCompleted: () => {},
      },
    },
    actions: undefined,
  });

export const useAssistantGenerateRoutesDialogByTemplateContext =
  (): AssistantGenerateRoutesDialogByTemplateContextValue =>
    useContext(AssistantGenerateRoutesDialogByTemplateContext);

const AssistantGenerateRoutesDialogByTemplateProvider = ({
  value,
  children,
}: ProviderProps<AssistantGenerateRoutesDialogByTemplateProviderProps>): ReactElement => {
  const [activeTab, setActiveTab] =
    useState<AssistantGenerateRoutesDialogTab>('form');

  const [isScheduleRouteEnabled, setIsScheduleRouteEnabled] =
    useState<boolean>(false);

  const [currentApiTransactionId, setCurrentApiTransactionId] =
    useState<Nullable<string>>(null);

  const [currentRoutes, setCurrentRoutes] = useState<ApiTransactionRoutes[]>(
    [],
  );

  const [isSuccesCompleted, setIsSuccesCompleted] = useState(false);

  const handleStepperSuccessComplete = useCallback(
    (apiTransactionId: string, routes: ApiTransactionRoutes[]) => {
      setIsSuccesCompleted(true);
      setCurrentApiTransactionId(apiTransactionId);
      setCurrentRoutes(routes);

      value.actions?.onStepperComplete?.(apiTransactionId, routes);
    },
    [value.actions],
  );

  return (
    <AssistantGenerateRoutesDialogByTemplateContext.Provider
      value={{
        ...value,
        activeTab: { tab: activeTab, setActiveTab },
        form: {
          isScheduleRouteEnabled: {
            value: isScheduleRouteEnabled,
            setValue: setIsScheduleRouteEnabled,
          },
        },
        stepper: {
          currentApiTransactionId: {
            value: currentApiTransactionId,
            setCurrentApiTransactionId: setCurrentApiTransactionId,
          },
          currentRoutes: {
            value: currentRoutes,
            setCurrentRoutes,
          },
          isSuccesCompleted: {
            value: isSuccesCompleted,
            setIsSuccesCompleted,
          },
        },
        actions: {
          ...value.actions,
          onStepperComplete: handleStepperSuccessComplete,
        },
      }}
    >
      {children}
    </AssistantGenerateRoutesDialogByTemplateContext.Provider>
  );
};

export default AssistantGenerateRoutesDialogByTemplateProvider;
